import { CvApiServiceBase } from './cv-api-service-base';
import { ClearViewUtils } from '../utils';
import { UserTimezoneInfo, MicrosoftTimeZoneInfo, DashboardTimezoneInfo, TimeZoneDictionary } from 'src/AngularJS/Admin/timezone.model';
import { ApiResponse } from 'src/AngularJS/Models/api-response.model';
import deepcopy from 'deepcopy';

export class TimezoneServiceBase {
    public microsoftTimeZoneInfos: Map<string, MicrosoftTimeZoneInfo>;
    public microsoftTimeZoneInfosLoading = false;
    public microsoftTimeZoneInfosTimer: number;
    public microsoftTimeZoneInfosReload: number;
    public getTimeZoneTimer: PromiseLike<void>;
    public microsftTimeZoneInfosLastLoaded: Date;
    public dataObj: UserTimezoneInfo;
    public filterDataObj: UserTimezoneInfo;

    // Clearview time zone name (Windows Registry Name) to IANA Time Zone Database name (for javascript to use)
    // Expand as more time zones are supported by the user settings
    public timeZoneConversionDictionary: TimeZoneDictionary = {
        "Mountain Standard Time": "America/Denver",
        "Dateline Standard Time": "Etc/GMT-12",
        "Hawaii Standard Time": "Pacific/Honolulu",
        "UTC-08": "Asia/Shanghai",
        "Yukon Standard Time": "America/Whitehorse",
        "Eastern Standard Time": "America/New_York",
        "UTC": "UTC",
        "GMT Standard Time": "UTC",
        "Greenwich Standard Time": "Europe/London",
        "W. Europe Standard Time": "Europe/Berlin",
        "Central European Standard Time": "Europe/Paris",
        "W. Central Africa Standard Time": "Africa/Lagos",
        "W. Mongolia Standard Time": "Asia/Hovd",
        "China Standard Time": "Asia/Shanghai",
        "W. Australia Standard Time": "Australia/Perth",
    };
    //Default time zone MST
    public DEFAULT_TIMEZONE: string = "America/Denver";

    constructor(
        fromSharedService: boolean = false,
        private cvApiService?: CvApiServiceBase,
    ) {
        if (fromSharedService)
            return TimezoneServiceBase.getServiceBase();

        if (!cvApiService)
            this.cvApiService = CvApiServiceBase.getServiceBase();

        if (this.cvApiService.apiToken) {
            const TIME_ZONE_INFO = window.localStorage.getItem("microsoftTimeZoneInfos");
            if (TIME_ZONE_INFO)
                this.microsoftTimeZoneInfos = new Map(JSON.parse(TIME_ZONE_INFO).map((tz: MicrosoftTimeZoneInfo) => [tz.Id, tz]));
            const LAST_LOADED = window.localStorage.getItem("microsftTimeZoneInfosLastLoaded");
            if (LAST_LOADED)
                this.microsftTimeZoneInfosLastLoaded = new Date(JSON.parse(LAST_LOADED));
            this.getMicrosoftTimeZoneInfos();

            this.loadUserTimezoneInfo().then((data) => {
                this.dataObj = deepcopy(data);
                //Create a new object without the current user default timezone
                const selectedTime = data.selectedTimeZone?.replace('Not Set', data.defaultCompanyTimeZone);
                const index = data.availableTimeZones?.indexOf(selectedTime);
                data.availableTimeZones?.splice(index, 1);
                this.filterDataObj = data;
            });
        }
    }

    static getServiceBase(): TimezoneServiceBase {
        return ClearViewUtils.getSharedService(TimezoneServiceBase, 'TimezoneServiceBase');
    }

    public getUserTimezoneInfo(): UserTimezoneInfo {
        if (this.dataObj) return this.dataObj;

        this.loadUserTimezoneInfo();
        return null;
    }
    public async getUserTimezoneInfoAsync(): Promise<UserTimezoneInfo> {
        await this.loadUserTimezoneInfo();
        return this.dataObj;
    }

    /**
     * Get the offset from the browser time based on the given time zone
     */
    public getTimezoneOffset(timezone: string) {
        // If timezone exists or is valid, return that timezone's offset
        if (this.microsoftTimeZoneInfos?.has(timezone)) {
            const browserOffset = new Date().getTimezoneOffset();
            const utcOffset = this.microsoftTimeZoneInfos.get(timezone).Offset / 60000; // Convert milliseconds to minutes
            const dst = this.microsoftTimeZoneInfos.get(timezone).InDaylightSavingTime ? 60 : 0;

            return utcOffset + browserOffset + dst;
        }

        // Otherwise, return the timezone offest of the browser
        return 0;
    }

    /**
     * Get the Microsoft TimeZoneInfo from the server.
     * Reload the TimeZoneInfo every 24 hours to adjust for time zone changes and day light saving times
     */
    public async getMicrosoftTimeZoneInfos() {
        // If microsoftTimeZoneInfos already exists and has been loaded within 24 hours, do not load again
        if (this.microsoftTimeZoneInfos) {
            const timeDiff = (new Date().getTime() - this.microsftTimeZoneInfosLastLoaded.getTime()) / 3600000; // Convert from millisecond to hour
            if (timeDiff < 24)
                return;
        }

        // If microsoftTimeZoneInfos is currently loading, try loading later
        if (this.microsoftTimeZoneInfosLoading) {
            if (this.microsoftTimeZoneInfosTimer != null) {
                clearTimeout(this.microsoftTimeZoneInfosTimer);
                this.microsoftTimeZoneInfos = null;
            }

            this.microsoftTimeZoneInfosTimer = window.setTimeout(() => {
                this.getMicrosoftTimeZoneInfos();
                this.microsoftTimeZoneInfosTimer = null;
            }, 1000, false);
        }

        this.microsoftTimeZoneInfosLoading = true;
        try {
            const url = `/api/v2/MicrosoftTimeZone/`;
            const response = await this.cvApiService.get<MicrosoftTimeZoneInfo[]>(url);
            if (response.status_code >= 200 && response.status_code < 300) {
                this.microsoftTimeZoneInfos = new Map(response.data.map(tz => [tz.Id, tz]));
                this.microsoftTimeZoneInfosLoading = false;
                this.microsftTimeZoneInfosLastLoaded = new Date();
                window.localStorage.setItem("microsoftTimeZoneInfos", JSON.stringify(response.data));
                window.localStorage.setItem("microsftTimeZoneInfosLastLoaded", JSON.stringify(this.microsftTimeZoneInfosLastLoaded));

                if (this.microsoftTimeZoneInfosReload != null) {
                    clearTimeout(this.microsoftTimeZoneInfosReload);
                    this.microsoftTimeZoneInfosReload = null;
                }

                this.microsoftTimeZoneInfosReload = window.setTimeout(() => {
                    this.getMicrosoftTimeZoneInfos();
                    this.microsoftTimeZoneInfosReload = null;
                }, 86400000, false); // Reload time zone every 24 hours
                return;
            }
            throw response;
        } catch (err) {
            console.error("Failed getting time zones infos");
            this.microsoftTimeZoneInfosLoading = false;
            this.getMicrosoftTimeZoneInfos();
        }
    }

    public async setUserTimezone(selectedTimezone: string) {
        if (this.dataObj.selectedTimeZone === selectedTimezone && !this.dataObj.availableTimeZones.includes(selectedTimezone))
            return false;

        try {
            const url = `/api/v2/userTimezoneInfo/`;
            const response = await this.cvApiService.post(url, JSON.stringify(selectedTimezone)) as ApiResponse<void>;
            if (response.status_code >= 200 && response.status_code < 300) {
                this.dataObj.selectedTimeZone = selectedTimezone;
                return true;
            }
            throw response;
        } catch (err) {
            console.error("Failed setting time zones info");
            throw err;
        }
    }

    public async getDashUserCompTimezone(dashboardKey: string, moduleKey: string): Promise<DashboardTimezoneInfo> {
        try {
            const url = `/api/v2/dashUserCompTimezone/${dashboardKey}/${moduleKey}`;
            const response = await this.cvApiService.get<DashboardTimezoneInfo>(url);
            if (response.status_code >= 200 && response.status_code < 300) {
                return response.data;
            }
            throw response;
        } catch (err) {
            console.error("Failed getting time zone");
            throw err;
        }

    }

    public async getDashboardTimezone(dashboardKey: string): Promise<DashboardTimezoneInfo> {
        try {
            const url = `/api/v2/dashboardTimezone/${dashboardKey}`;
            const response = await this.cvApiService.get<DashboardTimezoneInfo>(url);
            if (response.status_code >= 200 && response.status_code < 300) {
                return response.data;
            }
            throw response;
        } catch (err) {
            console.error("Failed getting time zone");
            throw err;
        }
    }

    public async setDashboardTimezone(dashboardKey: string, selectedTimezone: string): Promise<boolean> {
        if (selectedTimezone != null && !this.dataObj.availableTimeZones.includes(selectedTimezone))
            return false;

        try {
            const url = `/api/v2/dashboardTimezone/${dashboardKey}`;
            const response = await this.cvApiService.post<boolean>(url, JSON.stringify(selectedTimezone));
            if (response.status_code >= 200 && response.status_code < 300) {
                return response.data;
            }
            throw response;
        } catch (err) {
            console.error("Failed setting time zones info");
            throw err;
        }
    }

    private async loadUserTimezoneInfo() {
        try {
            const url = `/api/v2/userTimezoneInfo/`;
            const response = await this.cvApiService.get(url) as ApiResponse<UserTimezoneInfo>;
            if (response.status_code >= 200 && response.status_code < 300) {
                this.dataObj = response.data;
                return response.data;
            }
            throw response;
        } catch (err) {
            console.error("Failed getting time zones info");
            throw err;
        }
    };

    //Retrieve an accurate, time zone stamped, current Date/Time Value for the user set timezone. Regardless of user system time
    public async getCompanyLocalTimeAsync() {
        let timezone = await this.getUserTimezoneInfoAsync();
        let date = new Date()
        return new Date(date.toLocaleString('en-US', { timeZone: this.timeZoneConversionDictionary[timezone.selectedTimeZone] || this.DEFAULT_TIMEZONE }));
    }
}
